import React from "react"
import { generatePath, Link, Outlet } from "react-router-dom"
import { computed } from "mobx"
import { useLocalObservable } from "mobx-react"
import { observer } from "mobx-react-lite"
import { Table } from "antd"
import { ColumnsType } from "antd/es/table"

import { CreateButton } from "@src/app/components/CreateButton"
import { NoContent } from "@src/app/components/NoContent/Index"
import { IdItem } from "@src/app/components/tableRowItems/idItem"
import { configStore } from "@src/app/configStore"
import { DefaultPageLayout } from "@src/app/layouts/DefaultPageLayout"
import { paths } from "@src/app/paths"
import CheckoutsCollection from "@src/pages/checkouts/classes/CheckoutsCollection"
import { CheckoutResponse } from "@src/shared/api/generated"
import { paddingLimit } from "@src/shared/strings"
import css from "./index.module.css"

export const CheckoutsLayout: React.FC = () => {
  return <Outlet />
}

export const CheckoutsPage = observer(() => {
  const checkouts = useLocalObservable(() => new CheckoutsCollection())

  const {
    config: { payment_page_url },
    isLoading: configIsLoading,
  } = configStore

  const [isLoading, isEmpty] = computed(() => {
    return [checkouts.isLoading && checkouts.isEmpty, checkouts.isEmpty]
  }).get()

  const columns: ColumnsType<CheckoutResponse> = [
    {
      dataIndex: "id",
      render: (_, { id }) => <IdItem id={id as string}></IdItem>,
      title: "ID",
    },
    {
      dataIndex: "project_id",
      ellipsis: true,
      render: (_, { project_id }) => (
        <span>{paddingLimit(project_id, 0, 5)}</span>
      ),
      title: "Project id",
    },
    {
      dataIndex: "name",
      fixed: "left",
      render: (_, { id, name }) => (
        <Link
          id="GENERATED_ID_CHECKOUTS_LINK_VOID_1"
          to={generatePath(paths.checkout, { id: id as string })}
        >
          {name}
        </Link>
      ),
      title: "Name",
    },
    {
      align: "center",
      dataIndex: "description",
      ellipsis: true,
      title: "Description",
    },
    {
      dataIndex: "pricing_type",
      title: "Pricing type",
    },
    {
      dataIndex: "fiat_price",
      render: (_, { fiat_price }) => {
        if (!fiat_price) return null

        const { amount, symbol } = fiat_price

        const token = `${amount} (${symbol})`

        return <span style={{ whiteSpace: "nowrap" }}>{token}</span>
      },
      title: "Amount",
    },
    {
      dataIndex: "id",
      render: (_, { id }) => (
        <IdItem id={`${payment_page_url}/checkout/${id}`} />
      ),
      title: "Link",
    },
  ]

  return (
    <DefaultPageLayout
      button={
        <CreateButton
          className={css.button}
          text="Create"
          to={paths.checkout}
        />
      }
      className={css.page}
      emptyBlock={
        <NoContent
          paragraph="This section will contain all the Checkouts you have created"
          title="No Checkouts Yet"
        />
      }
      isEmpty={isEmpty}
      isLoading={isLoading}
      title="Checkouts"
    >
      <Table
        columns={columns}
        dataSource={checkouts.items}
        loading={checkouts.isLoading || configIsLoading}
        pagination={{
          onChange: checkouts.load,
          ...checkouts.pagination,
        }}
        scroll={{ x: true }}
      />
    </DefaultPageLayout>
  )
})
